import { useQuery } from "@tanstack/react-query"
import { Check, Plus, X } from "lucide-react"
import type { ReactNode } from "react"
import {
	type AudienceSegment,
	type AudienceStateFilter,
	SEGMENT_KEYS,
	type VariableFilter,
	type VariableFilterOp,
} from "@/entities/audience"
import { getNiches } from "@/entities/category"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu"
import { Input } from "@/shared/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover"
import { NativeSelect } from "@/shared/ui/native-select"
import { OP_LABELS } from "./variable-filter"

const BEGINNER_PATH_STEPS = 15
const MAX_VARIABLE_RULES = 10

type Activity = `read-${number}` | `idle-${number}`
type Beginner = "not_started" | "started" | "finished"

const ACTIVITY_OPTIONS: { value: Activity; label: string }[] = [
	{ value: "read-7", label: "смотрели за 7 дней" },
	{ value: "read-30", label: "смотрели за 30 дней" },
	{ value: "read-90", label: "смотрели за 90 дней" },
	{ value: "idle-14", label: "не смотрели 14 дней" },
	{ value: "idle-30", label: "не смотрели 30 дней" },
	{ value: "idle-90", label: "не смотрели 90 дней" },
]

type ConditionKind =
	| "channel"
	| "activity"
	| "niche"
	| "minReads"
	| "favorites"
	| "beginner"
	| "registeredFrom"
	| "registeredTo"

const APP_CONDITIONS: { kind: ConditionKind; label: string }[] = [
	{ kind: "channel", label: "Канал клуба" },
	{ kind: "activity", label: "Просмотр материалов" },
	{ kind: "niche", label: "Смотрели нишу" },
	{ kind: "minReads", label: "Посмотрели материалов" },
	{ kind: "favorites", label: "Избранное" },
	{ kind: "beginner", label: "Путь новичка" },
	{ kind: "registeredFrom", label: "Завели аккаунт с" },
	{ kind: "registeredTo", label: "Завели аккаунт по" },
]

const CLEARED: Record<ConditionKind, Partial<AudienceSegment>> = {
	channel: { channel: undefined },
	activity: { readWithinDays: undefined, notReadDays: undefined },
	niche: { nicheId: undefined },
	minReads: { minReads: undefined },
	favorites: { favorites: undefined },
	beginner: { beginner: undefined, beginnerMinSteps: undefined },
	registeredFrom: { registeredFrom: undefined },
	registeredTo: { registeredTo: undefined },
}

function activityOf(segment: AudienceSegment): Activity | null {
	if (segment.readWithinDays) return `read-${segment.readWithinDays}`
	if (segment.notReadDays) return `idle-${segment.notReadDays}`
	return null
}

function beginnerOf(segment: AudienceSegment): Beginner | null {
	if (segment.beginnerMinSteps && segment.beginnerMinSteps >= BEGINNER_PATH_STEPS) return "finished"
	return segment.beginner ?? null
}

function isActive(segment: AudienceSegment, kind: ConditionKind): boolean {
	switch (kind) {
		case "activity":
			return activityOf(segment) !== null
		case "niche":
			return !!segment.nicheId
		case "beginner":
			return beginnerOf(segment) !== null
		default:
			return segment[kind] !== undefined
	}
}

function daysAgo(days: number): string {
	return new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10)
}

export function pickSegment(source: AudienceSegment): AudienceSegment {
	return Object.fromEntries(
		SEGMENT_KEYS.flatMap((key) => {
			const value = source[key]
			if (value === undefined || (Array.isArray(value) && value.length === 0)) return []
			return [[key, value]]
		}),
	) as AudienceSegment
}

export function hasSegment(source: AudienceSegment): boolean {
	return Object.keys(pickSegment(source)).length > 0
}

/**
 * Условия отбора: по данным приложения и по ответам в этом боте, сколько угодно, все сразу.
 * На экране только добавленные условия; новое добавляется со значением по умолчанию.
 */
type EditorProps = {
	value: AudienceSegment
	onChange: (next: AudienceSegment) => void
	variables?: { name: string }[]
	/** Варианты списком, а не выпадающим полем: для плашек. */
	list?: boolean
	/** Не писать «условий нет»: условия уже заданы в другом месте. */
	hideEmptyHint?: boolean
}

function useSegmentEditor({ value, onChange, variables = [], list = false }: EditorProps) {
	const niches = useQuery({ queryKey: ["niches"], queryFn: getNiches, staleTime: 5 * 60_000 })
	const set = (patch: Partial<AudienceSegment>) => onChange(pickSegment({ ...value, ...patch }))
	const rules = value.variableRules ?? []
	const setRules = (next: VariableFilter[]) => set({ variableRules: next })
	const number = (raw: string) => {
		const parsed = Number.parseInt(raw, 10)
		return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
	}

	const add = (kind: ConditionKind) => {
		const defaults: Record<ConditionKind, Partial<AudienceSegment>> = {
			channel: { channel: "member" },
			activity: { readWithinDays: 30 },
			niche: { nicheId: niches.data?.[0]?.id },
			minReads: { minReads: 5 },
			favorites: { favorites: "has" },
			beginner: { beginner: "started" },
			registeredFrom: { registeredFrom: daysAgo(30) },
			registeredTo: { registeredTo: daysAgo(0) },
		}
		set(defaults[kind])
	}
	const remove = (kind: ConditionKind) => set(CLEARED[kind])

	const control = (kind: ConditionKind): ReactNode => {
		switch (kind) {
			case "channel":
				return (
					<Choice
						list={list}
						label="Канал клуба"
						value={value.channel ?? "member"}
						onChange={(channel) => set({ channel })}
						options={[
							{ value: "member", label: "сейчас в канале" },
							{ value: "not_member", label: "не в канале: вышли или не вступали" },
							{ value: "no_account", label: "нет аккаунта в приложении" },
						]}
					/>
				)
			case "activity":
				return (
					<Choice
						list={list}
						label="Просмотр материалов"
						value={activityOf(value) ?? "read-30"}
						onChange={(next) => {
							const [mode, days] = next.split("-")
							set({
								readWithinDays: mode === "read" ? Number(days) : undefined,
								notReadDays: mode === "idle" ? Number(days) : undefined,
							})
						}}
						options={ACTIVITY_OPTIONS}
					/>
				)
			case "niche":
				return (
					<Choice
						list={list}
						label="Ниша"
						value={value.nicheId ?? ""}
						onChange={(nicheId) => set({ nicheId: nicheId || undefined })}
						options={(niches.data ?? []).map((niche) => ({ value: niche.id, label: niche.name }))}
					/>
				)
			case "minReads":
				return (
					<div className="flex items-center gap-2">
						<span className="text-sm text-muted-foreground">от</span>
						<Input
							aria-label="Посмотрели материалов, от"
							type="number"
							min={1}
							className="h-9 w-24"
							value={value.minReads ?? ""}
							onChange={(event) => set({ minReads: number(event.target.value) ?? 1 })}
						/>
						<span className="text-sm text-muted-foreground">материалов</span>
					</div>
				)
			case "favorites":
				return (
					<Choice
						list={list}
						label="Избранное"
						value={value.favorites ?? "has"}
						onChange={(favorites) => set({ favorites })}
						options={[
							{ value: "has", label: "что-то добавили" },
							{ value: "none", label: "пустое" },
						]}
					/>
				)
			case "beginner":
				return (
					<Choice
						list={list}
						label="Путь новичка"
						value={beginnerOf(value) ?? "started"}
						onChange={(next) =>
							set({
								beginner: next === "finished" ? undefined : next,
								beginnerMinSteps: next === "finished" ? BEGINNER_PATH_STEPS : undefined,
							})
						}
						options={[
							{ value: "not_started", label: "не начинали" },
							{ value: "started", label: "отметили хотя бы шаг" },
							{ value: "finished", label: "прошли весь путь" },
						]}
					/>
				)
			case "registeredFrom":
			case "registeredTo":
				return (
					<Input
						aria-label={kind === "registeredFrom" ? "Завели аккаунт с" : "Завели аккаунт по"}
						type="date"
						className="h-9 w-44"
						value={value[kind] ?? ""}
						onChange={(event) => set({ [kind]: event.target.value || undefined })}
					/>
				)
		}
	}

	const active = APP_CONDITIONS.filter((condition) => isActive(value, condition.kind))
	const available = APP_CONDITIONS.filter((condition) => !isActive(value, condition.kind))

	return {
		value,
		list,
		variables,
		niches: niches.data ?? [],
		rules,
		setRules,
		control,
		add,
		remove,
		active,
		available,
	}
}

type Editor = ReturnType<typeof useSegmentEditor>

/** Выбор одного: выпадающее поле в строках или список вариантов внутри плашки. */
function Choice<T extends string>({
	list,
	label,
	value,
	options,
	onChange,
}: {
	list: boolean
	label: string
	value: T
	options: readonly { value: T; label: string }[]
	onChange: (value: T) => void
}) {
	if (!list) {
		return (
			<NativeSelect
				aria-label={label}
				value={value}
				onChange={(event) => onChange(event.target.value as T)}
			>
				{options.map((option) => (
					<option key={option.value} value={option.value}>
						{option.label}
					</option>
				))}
			</NativeSelect>
		)
	}
	return (
		<div role="radiogroup" aria-label={label} className="flex max-h-72 flex-col gap-0.5 overflow-y-auto">
			{options.map((option) => {
				const selected = option.value === value
				return (
					<button
						key={option.value}
						type="button"
						role="radio"
						aria-checked={selected}
						onClick={() => onChange(option.value)}
						className={cn(
							"flex items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
							selected && "bg-accent font-medium",
						)}
					>
						<Check className={cn("size-4 shrink-0", selected ? "opacity-100" : "opacity-0")} />
						{option.label}
					</button>
				)
			})}
		</div>
	)
}

function RuleEditor({ editor, index }: { editor: Editor; index: number }) {
	const { rules, setRules, variables, list } = editor
	const rule = rules[index]
	if (!rule) return null
	const patch = (next: Partial<VariableFilter>) =>
		setRules(rules.map((item, i) => (i === index ? { ...item, ...next } : item)))
	const variableOptions = variables.map((variable) => ({ value: variable.name, label: variable.name }))
	const opOptions = (Object.keys(OP_LABELS) as VariableFilterOp[]).map((op) => ({
		value: op,
		label: OP_LABELS[op],
	}))
	const needsValue = rule.op === "equals" || rule.op === "contains"
	const changeOp = (op: VariableFilterOp) =>
		patch({ op, value: op === "set" || op === "empty" ? undefined : rule.value })
	const valueInput = needsValue ? (
		<Input
			aria-label="Значение"
			className="h-9 w-40"
			placeholder="Значение"
			value={rule.value ?? ""}
			onChange={(event) => patch({ value: event.target.value })}
		/>
	) : null

	if (list) {
		return (
			<div className="space-y-3">
				<div>
					<p className="mb-1 px-2.5 text-xs text-muted-foreground">Переменная</p>
					<Choice
						list
						label="Переменная"
						value={rule.variable}
						options={variableOptions}
						onChange={(variable) => patch({ variable })}
					/>
				</div>
				<div>
					<p className="mb-1 px-2.5 text-xs text-muted-foreground">Условие</p>
					<Choice list label="Условие" value={rule.op} options={opOptions} onChange={changeOp} />
				</div>
				{valueInput ? <div className="px-0.5">{valueInput}</div> : null}
			</div>
		)
	}
	return (
		<div className="flex flex-wrap items-center gap-2">
			<Choice
				list={false}
				label="Переменная"
				value={rule.variable}
				options={variableOptions}
				onChange={(variable) => patch({ variable })}
			/>
			<Choice list={false} label="Условие" value={rule.op} options={opOptions} onChange={changeOp} />
			{valueInput}
		</div>
	)
}

function AddConditionMenu({
	editor,
	extra,
	children,
}: {
	editor: Editor
	extra?: ReactNode
	children: ReactNode
}) {
	const { available, add, variables, rules, setRules } = editor
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
			<DropdownMenuContent align="start" className="w-64">
				{extra ? (
					<>
						<DropdownMenuLabel>Диалог в боте</DropdownMenuLabel>
						{extra}
						<DropdownMenuSeparator />
					</>
				) : null}
				{available.length > 0 ? (
					<>
						<DropdownMenuLabel>В приложении</DropdownMenuLabel>
						{available.map((condition) => (
							<DropdownMenuItem key={condition.kind} onSelect={() => add(condition.kind)}>
								{condition.label}
							</DropdownMenuItem>
						))}
					</>
				) : null}
				<DropdownMenuSeparator />
				<DropdownMenuLabel>В этом боте</DropdownMenuLabel>
				{variables.length === 0 ? (
					<DropdownMenuItem disabled>В сценарии нет переменных</DropdownMenuItem>
				) : (
					<DropdownMenuItem
						disabled={rules.length >= MAX_VARIABLE_RULES}
						onSelect={() => setRules([...rules, { variable: variables[0]!.name, op: "set" }])}
					>
						Ответ в переменной
					</DropdownMenuItem>
				)}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

/** Условия строками: для рассылки, где места много. */
export function SegmentFilters(props: EditorProps) {
	const editor = useSegmentEditor(props)
	const { active, rules, setRules, control, remove } = editor
	return (
		<div className="space-y-2">
			{active.length === 0 && rules.length === 0 && !props.hideEmptyHint ? (
				<p className="text-sm text-muted-foreground">
					Условий нет: подходят все пользователи бота.
				</p>
			) : null}
			{active.map((condition) => (
				<ConditionRow
					key={condition.kind}
					label={condition.label}
					onRemove={() => remove(condition.kind)}
				>
					{control(condition.kind)}
				</ConditionRow>
			))}
			{rules.map((rule, index) => (
				<ConditionRow
					key={`${rule.variable}-${index}`}
					label="Ответ в боте"
					onRemove={() => setRules(rules.filter((_, i) => i !== index))}
				>
					<RuleEditor editor={editor} index={index} />
				</ConditionRow>
			))}
			<AddConditionMenu editor={editor}>
				<Button type="button" variant="outline" size="sm">
					<Plus />
					Условие
				</Button>
			</AddConditionMenu>
		</div>
	)
}

function FilterPill({
	label,
	title,
	onRemove,
	children,
}: {
	label: string
	title?: string
	onRemove: () => void
	children?: ReactNode
}) {
	const shell = (
		<span className="inline-flex h-8 items-center rounded-full border border-primary/40 bg-primary/10 pr-0.5 text-sm">
			{children ? (
				<PopoverTrigger asChild>
					<button
						type="button"
						className="h-full rounded-l-full pr-1 pl-3 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
					>
						{label}
					</button>
				</PopoverTrigger>
			) : (
				<span className="pr-1 pl-3">{label}</span>
			)}
			<Button
				type="button"
				variant="ghost"
				className="h-6 w-6 rounded-full p-0"
				aria-label={`Убрать условие «${label}»`}
				onClick={onRemove}
			>
				<X className="h-3.5 w-3.5" />
			</Button>
		</span>
	)
	if (!children) return shell
	return (
		<Popover>
			{shell}
			<PopoverContent align="start" className="w-auto min-w-64 max-w-[calc(100vw-2rem)] p-2">
				{title ? <p className="px-2.5 pt-1 pb-2 text-xs font-medium text-muted-foreground">{title}</p> : null}
				{children}
			</PopoverContent>
		</Popover>
	)
}

const STATE_TEXT: Record<AudienceStateFilter, string> = {
	waiting: "ждут ответа",
	idle: "закончили сценарий",
	blocked: "заблокировали бота",
}

export type PillExtras = {
	state: AudienceStateFilter | undefined
	onStateChange: (value: AudienceStateFilter | undefined) => void
	entryLinks: { id: string; code: string; name: string }[]
	entryLink: string
	onEntryLinkChange: (code: string) => void
	/** Группы сценария: человек может быть в нескольких. */
	tags?: { id: string; name: string }[]
	tagId?: string
	onTagChange?: (tagId: string) => void
	/** Ограничения, которые можно только убрать: блок воронки. */
	fixed?: { id: string; label: string; onClear: () => void }[]
}

/**
 * Все условия списка плашками в одну строку, одного вида: статус, метка, блок воронки,
 * данные приложения, ответы в боте. Плашка открывает настройку, крестик убирает,
 * «Фильтр» добавляет новое.
 */
export function AudiencePills({ extras, ...props }: EditorProps & { extras: PillExtras }) {
	const editor = useSegmentEditor({ ...props, list: true })
	const chips = segmentChips(props.value, editor.niches)
	const kinds = new Map<string, ConditionKind>(editor.active.map((c) => [c.kind, c.kind]))
	const { state, onStateChange, entryLinks, entryLink, onEntryLinkChange, fixed = [] } = extras
	const { tags = [], tagId = "", onTagChange } = extras
	const link = entryLinks.find((item) => item.code === entryLink)
	const tag = tags.find((item) => item.id === tagId)

	return (
		<>
			{state ? (
				<FilterPill
					title="Статус диалога"
					label={`Статус: ${STATE_TEXT[state]}`}
					onRemove={() => onStateChange(undefined)}
				>
					<Choice
						list
						label="Статус диалога"
						value={state}
						onChange={onStateChange}
						options={(Object.keys(STATE_TEXT) as AudienceStateFilter[]).map((key) => ({
							value: key,
							label: STATE_TEXT[key],
						}))}
					/>
				</FilterPill>
			) : null}
			{entryLink ? (
				<FilterPill
					title="Пришли по метке"
					label={`Метка: ${link?.name ?? entryLink}`}
					onRemove={() => onEntryLinkChange("")}
				>
					<Choice
						list
						label="Метка"
						value={entryLink}
						onChange={onEntryLinkChange}
						options={entryLinks.map((item) => ({ value: item.code, label: item.name }))}
					/>
				</FilterPill>
			) : null}
			{tagId && onTagChange ? (
				<FilterPill
					title="В группе"
					label={`Группа: ${tag?.name ?? "удалённая"}`}
					onRemove={() => onTagChange("")}
				>
					<Choice
						list
						label="Группа"
						value={tagId}
						onChange={onTagChange}
						options={tags.map((item) => ({ value: item.id, label: item.name }))}
					/>
				</FilterPill>
			) : null}
			{fixed.map((item) => (
				<FilterPill key={item.id} label={item.label} onRemove={item.onClear} />
			))}
			{chips.map((chip) => {
				const kind = kinds.get(chip.id)
				const ruleIndex = chip.id.startsWith("rule-") ? Number(chip.id.slice(5)) : -1
				return (
					<FilterPill
						key={chip.id}
						title={
							kind
								? APP_CONDITIONS.find((condition) => condition.kind === kind)?.label
								: "Ответ в боте"
						}
						label={chip.label}
						onRemove={() => props.onChange(chip.without(props.value))}
					>
						{kind ? editor.control(kind) : <RuleEditor editor={editor} index={ruleIndex} />}
					</FilterPill>
				)
			})}
			<AddConditionMenu
				editor={editor}
				extra={
					<>
						<DropdownMenuItem disabled={!!state} onSelect={() => onStateChange("waiting")}>
							Статус диалога
						</DropdownMenuItem>
						<DropdownMenuItem
							disabled={!!entryLink || entryLinks.length === 0}
							onSelect={() => onEntryLinkChange(entryLinks[0]?.code ?? "")}
						>
							Метка
						</DropdownMenuItem>
						{onTagChange ? (
							<DropdownMenuItem
								disabled={!!tagId || tags.length === 0}
								onSelect={() => onTagChange(tags[0]?.id ?? "")}
							>
								Группа
							</DropdownMenuItem>
						) : null}
					</>
				}
			>
				<Button
					type="button"
					variant="outline"
					className="h-8 rounded-full border-dashed px-3 text-sm text-muted-foreground"
				>
					<Plus />
					Фильтр
				</Button>
			</AddConditionMenu>
		</>
	)
}

function ConditionRow({
	label,
	onRemove,
	children,
}: {
	label: string
	onRemove: () => void
	children: ReactNode
}) {
	return (
		<div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-background/40 px-3 py-2">
			<span className="min-w-36 text-sm font-medium">{label}</span>
			<div className="min-w-0 flex-1">{children}</div>
			<Button
				type="button"
				variant="ghost"
				size="icon"
				className="size-8 shrink-0"
				aria-label={`Убрать условие «${label}»`}
				onClick={onRemove}
			>
				<X />
			</Button>
		</div>
	)
}

const ACTIVITY_TEXT = Object.fromEntries(
	ACTIVITY_OPTIONS.map((option) => [option.value, option.label]),
)

export type SegmentChip = {
	id: string
	label: string
	/** Сегмент без этого условия. */
	without: (segment: AudienceSegment) => AudienceSegment
}

/** Условия словами, по одному на условие: для подписей и кнопок «убрать». */
export function segmentChips(
	segment: AudienceSegment,
	niches: { id: string; name: string }[] = [],
): SegmentChip[] {
	const chips: SegmentChip[] = []
	const push = (kind: ConditionKind, label: string) =>
		chips.push({
			id: kind,
			label,
			without: (current) => pickSegment({ ...current, ...CLEARED[kind] }),
		})
	if (segment.channel === "member") push("channel", "сейчас в канале клуба")
	if (segment.channel === "not_member") push("channel", "не в канале клуба")
	if (segment.channel === "no_account") push("channel", "без аккаунта в приложении")
	const activity = activityOf(segment)
	if (activity) push("activity", ACTIVITY_TEXT[activity] ?? activity)
	if (segment.nicheId) {
		const name = niches.find((niche) => niche.id === segment.nicheId)?.name
		push("niche", name ? `смотрели нишу «${name}»` : "смотрели выбранную нишу")
	}
	if (segment.minReads) push("minReads", `посмотрели от ${segment.minReads} материалов`)
	if (segment.favorites === "has") push("favorites", "есть избранное")
	if (segment.favorites === "none") push("favorites", "избранное пустое")
	const beginner = beginnerOf(segment)
	if (beginner === "not_started") push("beginner", "не начинали путь новичка")
	if (beginner === "started") push("beginner", "начали путь новичка")
	if (beginner === "finished") push("beginner", "прошли путь новичка")
	if (segment.registeredFrom) push("registeredFrom", `аккаунт с ${segment.registeredFrom}`)
	if (segment.registeredTo) push("registeredTo", `аккаунт по ${segment.registeredTo}`)
	;(segment.variableRules ?? []).forEach((rule, index) => {
		chips.push({
			id: `rule-${index}`,
			label: `${rule.variable} ${OP_LABELS[rule.op]}${rule.value ? ` «${rule.value}»` : ""}`,
			without: (current) =>
				pickSegment({
					...current,
					variableRules: (current.variableRules ?? []).filter((_, i) => i !== index),
				}),
		})
	})
	return chips
}

/** Условия словами, для строки «кому уйдёт». */
export function describeSegment(
	segment: AudienceSegment,
	niches: { id: string; name: string }[] = [],
): string[] {
	return segmentChips(segment, niches).map((chip) => chip.label)
}
